import type { Item, Vec2 } from '../hp/types';
import { isFlatDecor, isSurfaceFurniture, obstructsWhenPlaced } from '../hp/furnitureClass';
import { isRegion } from '../hp/regions';
import { itemFootprint, localFootprint, type Footprints } from './footprint';
import { isAttic } from './attic';

/**
 * What the piece being placed has been driven into, for the red outline.
 *
 * Unity outlines the obstruction in red for as long as it is obstructed
 * (`TopFurnitureHandler.ChangeHighlightingTablePlanes` → `SelectionView.Show(…, Color.red)`), and
 * the question it asks first is not geometric but catalogue: *may these two be in each other's
 * way at all?* A vase on a console, a tray on a sideboard, a cooktop in a counter, a rug under a
 * table — those are arrangements, not mistakes, and they are excluded by class before any box is
 * compared. Only pieces that provide a surface are checked against each other, and flat decor
 * only against flat decor. `src/core/hp/furnitureClass.ts` holds those classes.
 *
 * What is left is geometry, and here the web differs from the native app: Unity intersects real
 * shape polygons, we have oriented footprint boxes — the same ones snapping and the room clamp
 * run on. A box is the coarser instrument (it fills the gap under a seat, the swing of a door),
 * so the tolerances below absorb the difference rather than tighten the rule.
 */

/** Millimetre interpenetration is how a run of cabinets is authored, not a misplacement. */
const MIN_PENETRATION = .03;
/** Bigger pieces earn a proportionally bigger allowance before it reads as one. */
const PENETRATION_SHARE = .1;
/** Bodies at the same height, not one resting on the other — but never more than half of the
 * thinner body, or no two rugs could ever be inside one another. */
const MIN_VERTICAL = .03;

function planExtents(item: Item, footprints?: Footprints): { w: number; d: number } {
  const bounds = localFootprint(item, footprints);
  return { w: bounds.maxX - bounds.minX, d: bounds.maxZ - bounds.minZ };
}

/** Separating-axis overlap of two oriented footprints: the shallowest of the four box-edge
 * projections, or 0 as soon as one of them holds the pair apart. */
export function penetration(a: readonly Vec2[], b: readonly Vec2[]): number {
  let shallowest = Infinity;
  for (const [own, other] of [[a, b], [b, a]] as const) {
    for (const corner of [1, 3]) {
      const edge = { x: own[corner].x - own[0].x, z: own[corner].z - own[0].z };
      const length = Math.hypot(edge.x, edge.z);
      if (length < 1e-9) continue;
      const project = (points: readonly Vec2[]) => points.map(p => (p.x * edge.x + p.z * edge.z) / length);
      const mine = project(own), theirs = project(other);
      const overlap = Math.min(Math.max(...mine), Math.max(...theirs)) - Math.max(Math.min(...mine), Math.min(...theirs));
      if (overlap <= 0) return 0;
      shallowest = Math.min(shallowest, overlap);
    }
  }
  return shallowest === Infinity ? 0 : shallowest;
}

/** True when `other` is in the way of the `active` piece being placed — its class is one the
 * active piece can obstruct, and their bodies are inside one another far enough to read as a
 * misplacement rather than as how the two were built to sit together. */
export function isObstruction(active: Item, other: Item, footprints?: Footprints): boolean {
  if (active.id === other.id) return false;
  // A mansard's slope comes down over the floor: a piece standing or hanging under it is in its way where it rises through it.
  if (isAttic(other)) return !isAttic(active) && active.placement !== 'ceiling' && !isRegion(active) && obstructsWhenPlaced(active)
    && throughSlope(active, other, footprints);
  // Regions are the room itself; a wall niche is not furniture in anyone's way.
  if (active.placement !== other.placement) return false;
  if (isRegion(active) || isRegion(other) || !obstructsWhenPlaced(active)) return false;
  if (isSurfaceFurniture(active) && !isSurfaceFurniture(other)) return false;
  if (isFlatDecor(active) && !isFlatDecor(other)) return false;
  const vertical = Math.min(active.elevation + active.size.h, other.elevation + other.size.h)
    - Math.max(active.elevation, other.elevation);
  if (vertical <= Math.min(MIN_VERTICAL, Math.min(active.size.h, other.size.h) / 2)) return false;
  const one = planExtents(active, footprints), two = planExtents(other, footprints);
  const narrowest = Math.min(one.w, one.d, two.w, two.d);
  return penetration(itemFootprint(active, footprints), itemFootprint(other, footprints))
    > Math.max(MIN_PENETRATION, narrowest * PENETRATION_SHARE);
}

/** How far a piece may reach into a slope before it reads as standing through it: a skirting's or a frame's thickness. */
const SLOPE_ALLOWANCE = .02;

/**
 * Whether `active` rises through the slope of the mansard `wedge` (`src/state/attic.ts`): over the part of its footprint
 * under the wedge, its top stands higher than the slope does there. The slope rises from the wedge's back at its knee
 * to its top across its depth, in the wedge's own frame — x across its width from its middle, z into the room.
 */
function throughSlope(active: Item, wedge: Item, footprints?: Footprints): boolean {
  const { w, h, d } = wedge.size, top = active.elevation + active.size.h;
  if (!(w > 0 && h > 0 && d > 0) || top <= wedge.elevation + SLOPE_ALLOWANCE || active.elevation >= wedge.elevation + h) return false;
  const c = Math.cos(wedge.rotation), s = Math.sin(wedge.rotation);
  let piece = itemFootprint(active, footprints).map(p => { const dx = p.x - wedge.position.x, dz = p.z - wedge.position.z; return { x: dx * c + dz * s, z: -dx * s + dz * c }; });
  for (const [ax, az, limit] of [[1, 0, w / 2], [-1, 0, w / 2], [0, -1, 0], [0, 1, d]] as const) {
    // Keep the part where ax·x + az·z ≤ limit.
    const out: Vec2[] = [], f = (p: Vec2) => limit - (ax * p.x + az * p.z);
    piece.forEach((p, i) => {
      const q = piece[(i + 1) % piece.length], fp = f(p), fq = f(q);
      if (fp >= 0) out.push(p);
      if ((fp > 0 && fq < 0) || (fp < 0 && fq > 0)) { const t = fp / (fp - fq); out.push({ x: p.x + (q.x - p.x) * t, z: p.z + (q.z - p.z) * t }); }
    });
    piece = out;
    if (piece.length < 3) return false;
  }
  const lowest = Math.min(...piece.map(p => wedge.elevation + h * Math.max(0, p.z) / d));
  return top > lowest + SLOPE_ALLOWANCE;
}

/** The ids of everything standing in the way of the piece being placed. */
export function obstructions(items: readonly Item[], active: Item, footprints?: Footprints): string[] {
  if (!obstructsWhenPlaced(active)) return [];
  return items.filter(item => isObstruction(active, item, footprints)).map(item => item.id);
}
